import { FunctionComponent, useEffect, useState } from 'react'
import moment, { Moment } from 'moment/moment'
import { CalendarEventType, EventCalendar } from '@/types/Calendar.ts'
import { Daycare } from '@/types/Daycare.ts'
import styles from './calendarStaff.module.scss'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { ArrowBack, ArrowForward } from '@mui/icons-material'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import {
    getDaycareClosing,
    getDaycareDaysOpening,
    getDaycareOpening,
    getNextOpeningDate,
    getPreviousOpeningDate
} from '@/modules/helper/daycareHelper.ts'
import { StaffWithLinks } from '@/types/Staff.ts'
import StaffWorkTableRow from './staffWork/staffWorkTableRow.tsx'
import Desktop from '@/modules/desktopMobile/desktop.tsx'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import StaffEventsTableRow from './staffEvents/staffEventsTableRow.tsx'
import ButtonIcon from '@/components/_custom/Buttons/ButtonIcon/ButtonIcon.tsx'
import { CalendarIcon } from '@mui/x-date-pickers'
import DatePickerDialog from '../../Dialog/datePicker/datePickerDialog.tsx'
import { format } from 'date-fns'
import { StaffSlots } from '@/types/StaffRate.ts'
import { CalendarView } from '../../utils/calendarParameters.ts'
import DraftingStaffSlots from '@/container/drafting/list/DraftingStaffSlots'
import ShowSlot from '../../Dialog/showEvent'
import { Work } from '@/types/work.ts'
import { getEffectiveDayBounds } from '@/container/drafting/TimBarSlots/defineSlots.ts'
import { FetchPlanningDataProps } from '../../hooks/usePlanningSlots.ts'

interface CalendarStaffProps {
    fetchData: (props: FetchPlanningDataProps) => void
    calendarEvents: EventCalendar[]
    currentDaycare: Daycare
    staffsListData: Array<StaffWithLinks>
    isMobile: boolean
    refetchData: () => void
    currentView: string
}

const CalendarStaff: FunctionComponent<CalendarStaffProps> = ({
    fetchData,
    calendarEvents,
    currentDaycare,
    staffsListData,
    refetchData,
    isMobile,
    currentView
}: CalendarStaffProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const isSingleDayMode: boolean = isMobile || currentView === CalendarView.OneDay
    const isDayView: boolean = !isMobile && currentView === CalendarView.OneDay

    const openDays: number[] = getDaycareDaysOpening(currentDaycare)
    const columnWidth: string = openDays ? `${70 / openDays.length}%` : '0%'

    const weekDays: { isOpen: boolean; dayIndex: number; labelKey: string }[] = [
        { isOpen: !!currentDaycare.opening.monOpen, dayIndex: 0, labelKey: 'mondayShort' },
        { isOpen: !!currentDaycare.opening.tueOpen, dayIndex: 1, labelKey: 'tuesdayShort' },
        { isOpen: !!currentDaycare.opening.wedOpen, dayIndex: 2, labelKey: 'wednesdayShort' },
        { isOpen: !!currentDaycare.opening.thuOpen, dayIndex: 3, labelKey: 'thursdayShort' },
        { isOpen: !!currentDaycare.opening.friOpen, dayIndex: 4, labelKey: 'fridayShort' },
        { isOpen: !!currentDaycare.opening.satOpen, dayIndex: 5, labelKey: 'saturdayShort' },
        { isOpen: !!currentDaycare.opening.sunOpen, dayIndex: 6, labelKey: 'sundayShort' }
    ]

    const [isDatePickerDialogOpen, setDatePickerDialogOpen] = useState(false)

    function getAdjustedStartDate(openDays: number[], baseDate: Moment): Moment {
        const adjustedDate: Moment = baseDate.clone().startOf('week')

        for (let i: number = 0; i < 7; i++) {
            const dayIndex: number = (adjustedDate.day() + 6) % 7
            if (openDays.includes(dayIndex)) {
                return adjustedDate
            }
            adjustedDate.add(1, 'day')
        }

        return baseDate
    }

    function getAdjustedEndDate(openDays: number[], baseDate: Moment): Moment {
        const adjustedDate: Moment = baseDate.clone().endOf('week')

        for (let i: number = 7; i > 0; i--) {
            const dayIndex: number = (adjustedDate.day() + 6) % 7
            if (openDays.includes(dayIndex)) {
                return adjustedDate
            }
            adjustedDate.subtract(1, 'day')
        }

        return baseDate
    }

    const [startOfWeek, setStartOfWeek] = useState(moment().startOf('week'))
    const [endOfWeek, setEndOfWeek] = useState(moment().endOf('week'))

    const [startDate, setStartDate] = useState(isSingleDayMode ? moment() : getAdjustedStartDate(openDays, moment()))
    const [endDate, setEndDate] = useState(isSingleDayMode ? moment() : getAdjustedEndDate(openDays, moment()))

    const [calendarTitle, setCalendarTitle] = useState('')

    function handlePrev(): void {
        setNewCalendarTitle('subtract')
    }

    function handleNext(): void {
        setNewCalendarTitle('add')
    }

    function setNewCalendarTitle(
        action: 'add' | 'subtract' | 'none' = 'none',
        forceStartDate?: Moment,
        forceEndDate?: Moment
    ): void {
        let start: Moment | undefined
        let end: Moment | undefined

        if (action === 'add') {
            if (isSingleDayMode) {
                start = getNextOpeningDate(currentDaycare, startDate.clone().add(1, 'd'))
                end = start.clone()
            } else {
                start = startDate.clone().add(1, 'w')
                end = endDate.clone().add(1, 'w')
                setStartOfWeek(startOfWeek.clone().add(1, 'w'))
                setEndOfWeek(endOfWeek.clone().add(1, 'w'))
            }
        } else if (action === 'subtract') {
            if (isSingleDayMode) {
                start = getPreviousOpeningDate(currentDaycare, startDate.clone().subtract(1, 'd'))
                end = start.clone()
            } else {
                start = startDate.clone().subtract(1, 'w')
                end = endDate.clone().subtract(1, 'w')
                setStartOfWeek(startOfWeek.clone().subtract(1, 'w'))
                setEndOfWeek(endOfWeek.clone().subtract(1, 'w'))
            }
        } else {
            start = forceStartDate ? forceStartDate.clone() : startDate.clone()
            end = forceEndDate ? forceEndDate.clone() : endDate.clone()
            if (forceStartDate && forceEndDate) {
                setStartOfWeek(forceStartDate)
                setEndOfWeek(forceEndDate)
            }
        }

        if (start && end) {
            fetchData({ fromDate: start, toDate: end, isGlobalPlanning: false })

            setCalendarTitle(
                isSingleDayMode
                    ? format(start.toDate(), 'EEEE dd MMMM', { locale: getLocalFromLanguage() })
                    : `${t('fromDate')}
                    ${format(start.toDate(), 'EEEE dd MMMM', { locale: getLocalFromLanguage() })} ` +
                          `${t('toDate').toLowerCase()}
                          ${format(end.toDate(), 'EEEE dd MMMM yyyy', { locale: getLocalFromLanguage() })}`
            )

            setStartDate(start)
            setEndDate(end)
        }
    }

    useEffect(() => {
        const forceStart: Moment = isSingleDayMode ? moment() : getAdjustedStartDate(openDays, moment())
        const forceEnd: Moment = isSingleDayMode ? moment() : getAdjustedEndDate(openDays, moment())

        setNewCalendarTitle('none', forceStart, forceEnd)
    }, [currentDaycare, isSingleDayMode])

    function handleSelectDatePicker(selectedDatePickerDate: Moment): void {
        const forceStart: Moment = isSingleDayMode
            ? selectedDatePickerDate
            : getAdjustedStartDate(openDays, selectedDatePickerDate.clone().startOf('week'))
        const forceEnd: Moment = isSingleDayMode
            ? selectedDatePickerDate
            : getAdjustedStartDate(openDays, selectedDatePickerDate.clone().endOf('week'))

        setNewCalendarTitle('none', forceStart, forceEnd)
    }

    const [closingDayAndEvent, setClosingDayAndEvent] = useState<EventCalendar[]>([])

    useEffect(() => {
        setClosingDayAndEvent(
            calendarEvents.filter(
                (event: EventCalendar): boolean => event.type === 'CLOSING_DAY' || event.type === 'EVENT'
            )
        )
    }, [calendarEvents])

    // Vue jour : on réutilise calendarEvents (déjà chargé pour la vue semaine, via getWorks) plutôt
    // qu'une requête séparée type GET_RATE_SLOTS_AND_SLOTS, qui filtre par section_group et exclut donc
    // les works "hors section" (section_group null en base).
    const staffEventTypes: CalendarEventType[] = [
        CalendarEventType.IN_SECTION,
        CalendarEventType.NOT_IN_SECTION,
        CalendarEventType.STAFF_HOLIDAY,
        CalendarEventType.STAFF_TRAINING
    ]

    const allWorksForDay: Work[] = calendarEvents
        .filter((event: EventCalendar): boolean => staffEventTypes.includes(event.type))
        .map((event: EventCalendar): Work => event.slots as Work)
        .filter((work: Work): boolean => moment(work.date, 'DD-MM-YYYY').isSame(startDate, 'day'))

    const visibleStaffSlots: StaffSlots[] = staffsListData.map(
        (staff: StaffWithLinks): StaffSlots => ({
            staffId: staff.id,
            staffName: `${staff.firstname} ${staff.lastname}`,
            self: staff.email === user?.email,
            works: allWorksForDay.filter((work: Work): boolean => work.staff === staff.id)
        })
    )

    const dayOfWeek: number = (startDate.day() + 6) % 7
    const dayOpening: string = getDaycareOpening(currentDaycare, dayOfWeek) || '08:00'
    const dayClosing: string = getDaycareClosing(currentDaycare, dayOfWeek) || '17:30'

    const { opening: effectiveOpening, closing: effectiveClosing } = getEffectiveDayBounds(
        allWorksForDay,
        dayOpening,
        dayClosing
    )

    const [showSlotDialogOpen, setShowSlotDialogOpen] = useState<boolean>(false)
    const [selectedEvent, setSelectedEvent] = useState<EventCalendar | null>(null)

    return (
        <div className={styles.TableAndHeader}>
            <div className={styles.Header}>
                <ButtonTextIcon
                    type={'custom'}
                    onClick={handlePrev}
                    customText={undefined}
                    customIcon={<ArrowBack />}
                    className={styles.ButtonArrow}
                />
                <div className={styles.HeaderTitle} onClick={() => setDatePickerDialogOpen(true)}>
                    <p className={styles.Title}>{calendarTitle}</p>
                    <Desktop>
                        <ButtonIcon
                            icon={<CalendarIcon />}
                            onClick={() => setDatePickerDialogOpen(true)}
                            className={styles.Icon}
                        />
                    </Desktop>
                </div>
                <ButtonTextIcon
                    type={'custom'}
                    onClick={handleNext}
                    customText={undefined}
                    customIcon={<ArrowForward />}
                    className={styles.ButtonArrow}
                />
            </div>
            {isDayView ? (
                <div className={styles.StaffDayContainer}>
                    {visibleStaffSlots.length > 0 ? (
                        [...visibleStaffSlots]
                            .sort((a: StaffSlots, b: StaffSlots) => {
                                if (a.self !== b.self) return a.self ? -1 : 1
                                return a.staffName.localeCompare(b.staffName)
                            })
                            .map((staffSlots: StaffSlots) => (
                                <DraftingStaffSlots
                                    key={staffSlots.staffId}
                                    staffSlots={staffSlots}
                                    opening={effectiveOpening}
                                    closing={effectiveClosing}
                                    date={startDate.format('DD-MM-YYYY')}
                                    daycare={currentDaycare}
                                    staffsListData={staffsListData}
                                    allWorks={allWorksForDay}
                                    canWriteStaffWorks={!!user?.currentDaycareRights.canWriteStaffWorks}
                                    refetchData={refetchData}
                                    setSelectedEvent={setSelectedEvent}
                                    setShowSlotDialogOpen={setShowSlotDialogOpen}
                                />
                            ))
                    ) : (
                        <div className={styles.NoStaffText}>
                            <p>{t('noStaff')}</p>
                        </div>
                    )}
                </div>
            ) : (
                <TableContainer className={styles.TablePlacement}>
                    <Table className={styles.Table}>
                        <Desktop>
                            <TableHead className={styles.TableHeader}>
                                <TableRow>
                                    <TableCell sx={{ width: '30%' }} />
                                    {weekDays.map(
                                        (day) =>
                                            day.isOpen && (
                                                <TableCell
                                                    key={day.dayIndex}
                                                    className={styles.Text}
                                                    align={'center'}
                                                    sx={{ width: columnWidth }}
                                                >
                                                    {t(day.labelKey)}{' '}
                                                    {startOfWeek.clone().add(day.dayIndex, 'd').format('DD/MM')}
                                                </TableCell>
                                            )
                                    )}
                                </TableRow>
                            </TableHead>
                        </Desktop>
                        <TableBody className={styles.TableBody}>
                            {staffsListData.length === 0 && calendarEvents.length === 0 ? (
                                <TableRow>
                                    <TableCell className={styles.Text} align={'center'} colSpan={openDays.length + 1}>
                                        {t('noStaffs')}...
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {closingDayAndEvent.length > 0 && (
                                        <StaffEventsTableRow
                                            calendarEvents={closingDayAndEvent}
                                            isMobile={isMobile}
                                            currentDaycare={currentDaycare}
                                            columnWidth={columnWidth}
                                            calendarStartDate={startDate}
                                            calendarEndDate={endDate}
                                        />
                                    )}
                                    {staffsListData
                                        .sort((a: StaffWithLinks, b: StaffWithLinks) => {
                                            if (a.email === user?.email) return -1
                                            if (b.email === user?.email) return 1
                                            return a.firstname.localeCompare(b.firstname)
                                        })
                                        .map((staff: StaffWithLinks, index: number) => (
                                            <StaffWorkTableRow
                                                staff={staff}
                                                calendarEvents={calendarEvents}
                                                key={index}
                                                currentDaycare={currentDaycare}
                                                columnWidth={columnWidth}
                                                startOfWeek={isMobile ? startDate : startOfWeek}
                                                staffsListData={staffsListData}
                                                refetchData={refetchData}
                                                isMobile={isMobile}
                                            />
                                        ))}
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {isDatePickerDialogOpen && (
                <DatePickerDialog
                    open={isDatePickerDialogOpen}
                    handleClose={() => setDatePickerDialogOpen(false)}
                    selectedDate={startDate}
                    handleDateChange={handleSelectDatePicker}
                    currentDaycare={currentDaycare}
                />
            )}
            {user && showSlotDialogOpen && selectedEvent && (
                <ShowSlot
                    open={showSlotDialogOpen}
                    handleClose={() => setShowSlotDialogOpen(false)}
                    selectedEvent={selectedEvent}
                    daycare={currentDaycare}
                    refetchData={refetchData}
                    childs={[]}
                    staffsListData={staffsListData}
                    calendarEvents={[]}
                    places={[]}
                    isParent={false}
                    works={allWorksForDay}
                    canEditRegular={user.currentDaycareRights.canWriteContracts}
                    canEditStaffWorks={user.currentDaycareRights.canWriteStaffWorks}
                    lastRegularSlotDate={moment().add(1, 'year').format('DD-MM-YYYY')}
                />
            )}
        </div>
    )
}

export default CalendarStaff
